import type { ServiceWorkSlide } from "@/lib/content/work-carousel";
import {
  CONNECTED_SYSTEMS_FULL_TAGLINE,
  GROWTH_SERVICE_FULL_TAGLINE,
  MEDICAL_SYSTEMS_FULL_TAGLINE,
} from "@/lib/content/service-configs";
import { SITE_SURFACES } from "@/lib/navigation/site-surfaces";
import { shapeAsset } from "@/lib/shapes";

export type ServiceBucketId = "connected" | "growth" | "medical";

export type ServiceBucketConfig = {
  id: ServiceBucketId;
  title: string;
  href: string;
  icon: string;
  description: string;
  slideCategory: string;
  slideExcludeIds?: string[];
};

function serviceSurfaceForBucket(id: ServiceBucketId) {
  const s = SITE_SURFACES.find((x) => x.id === id);
  if (!s) {
    throw new Error(
      `SERVICE_BUCKETS: missing surface id "${id}" in SITE_SURFACES`,
    );
  }
  return s;
}

type ServiceBucketBase = {
  id: ServiceBucketId;
  icon: string;
  description: string;
  slideCategory: string;
  slideExcludeIds?: string[];
};

const SERVICE_BUCKET_BASE: readonly ServiceBucketBase[] = [
  {
    id: "connected",
    icon: shapeAsset("pyramid", "yellow"),
    description: CONNECTED_SYSTEMS_FULL_TAGLINE,
    slideCategory: "connected-systems",
  },
  {
    id: "growth",
    icon: shapeAsset("ball", "pink"),
    description: GROWTH_SERVICE_FULL_TAGLINE,
    slideCategory: "digital-experiences",
    slideExcludeIds: ["covid-school-hub"],
  },
  {
    id: "medical",
    icon: shapeAsset("cube", "teal"),
    description: MEDICAL_SYSTEMS_FULL_TAGLINE,
    slideCategory: "medical-devices",
  },
];

export const SERVICE_BUCKETS: readonly ServiceBucketConfig[] =
  SERVICE_BUCKET_BASE.map((b) => {
    const s = serviceSurfaceForBucket(b.id);
    return {
      id: b.id,
      title: s.label,
      href: s.path,
      icon: b.icon,
      description: b.description,
      slideCategory: b.slideCategory,
      ...(b.slideExcludeIds != null
        ? { slideExcludeIds: b.slideExcludeIds }
        : {}),
    };
  });

export type ExpertiseCategoryKey =
  | "strategy"
  | "design"
  | "development"
  | "prototyping"
  | "brand"
  | "marketing"
  | "ai-consulting";

export const EXPERTISE_ORDER: readonly ExpertiseCategoryKey[] = [
  "strategy",
  "design",
  "development",
  "prototyping",
  "brand",
  "marketing",
  "ai-consulting",
] as const;

export const EXPERTISE_LABELS: Readonly<Record<ExpertiseCategoryKey, string>> =
  {
    strategy: "Product + Tech Strategy",
    design: "Product Design",
    development: "Product Development",
    prototyping: "Prototyping and MVPs",
    brand: "Brand Consulting",
    marketing: "Marketing Sites",
    "ai-consulting": "AI Consulting",
  };

export type FooterSocialConfig = {
  primary: {
    label: string;
    href: string;
    description: string;
  };
  others: Array<{
    label: string;
    href: string;
    description: string;
  }>;
};

function pathForFooterSurfaceId(id: string): string {
  const s = SITE_SURFACES.find((x) => x.id === id);
  if (!s) {
    throw new Error(`FOOTER_COMPANY_LINK_SPECS: unknown surface id "${id}"`);
  }
  return s.path;
}

/** Footer labels + shapes here; `href` always matches `SITE_SURFACES` paths. */
const FOOTER_COMPANY_LINK_SPECS = [
  { surfaceId: "home", label: "About Us", shape: shapeAsset("ball", "pink") },
  { surfaceId: "work", label: "Our Work", shape: shapeAsset("cube", "blue") },
  { surfaceId: "team", label: "Team", shape: shapeAsset("arch", "coral") },
  { surfaceId: "labs", label: "Labs", shape: shapeAsset("ball", "purple") },
] as const;

export const FOOTER_COMPANY_LINKS: ReadonlyArray<{
  label: string;
  href: string;
  shape: string;
}> = FOOTER_COMPANY_LINK_SPECS.map((row) => ({
  label: row.label,
  href: pathForFooterSurfaceId(row.surfaceId),
  shape: row.shape,
}));

export const FOOTER_SOCIAL: FooterSocialConfig = {
  primary: {
    label: "LinkedIn",
    href: "https://linkedin.com/company/townshipagency",
    description: "This is where we're most active",
  },
  others: [
    {
      label: "Instagram",
      href: "https://instagram.com/townshipagency",
      description: "Tag @townshipagency in your story!",
    },
    {
      label: "Twitter",
      href: "https://twitter.com/townshipagency",
      description: "We check it sometimes",
    },
  ],
};

export type ServiceBucketSlides = Readonly<
  Record<ServiceBucketId, ServiceWorkSlide[]>
>;
